import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Paper, Typography, Card, CardContent,
  List, ListItemText, Chip, Alert, CircularProgress, Divider,
  ListItemButton, Avatar, Badge
} from '@mui/material';
import {
  People, TimeToLeave, Work, Assessment, TrendingUp,
  NotificationsActive, ArrowForward, HourglassTop, CheckCircleOutline
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { displayName } from '../types';
import EmployeeDashboard from '../components/EmployeeDashboard';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ totalEmployees: 0, pendingLeaveRequests: 0, openRequisitions: 0, activeProbations: 0 });
  const [recentActivities, setRecentActivities] = useState<any[]>([]);
  const [pendingLeaves, setPendingLeaves] = useState<any[]>([]);
  const [probations, setProbations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.role !== 'hr_admin') { setLoading(false); return; }
    Promise.all([
      apiService.getEmployees().catch(() => []),
      apiService.getLeaveRequests().catch(() => []),
      apiService.getRequisitions().catch(() => []),
      apiService.getActiveProbations().catch(() => []),
    ]).then(([employees, leaveRequests, requisitions, probationList]) => {
      const pending = leaveRequests.filter((r: any) => r.status === 'pending');
      setStats({
        totalEmployees: employees.length,
        pendingLeaveRequests: pending.length,
        openRequisitions: requisitions.filter((r: any) => r.status === 'open').length,
        activeProbations: probationList.length,
      });
      setPendingLeaves(pending.slice(0, 8));
      setProbations(probationList.slice(0, 5));
    }).catch(() => setError('Failed to load dashboard data'));
    apiService.getRecentActivities(20).then(setRecentActivities).catch(() => {});
    setLoading(false);
  }, [user?.role]);

  if (user?.role === 'employee') return <EmployeeDashboard />;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  const statCards = [
    { title: 'Total Employees', value: stats.totalEmployees, icon: <People />, color: '#C08A2E', bg: '#F3E6CD', path: '/employees' },
    { title: 'Pending Leave', value: stats.pendingLeaveRequests, icon: <TimeToLeave />, color: '#9E6F22', bg: '#F5EDE8', path: '/leave' },
    { title: 'Open Positions', value: stats.openRequisitions, icon: <Work />, color: '#2E7D5B', bg: '#EAF3EC', path: '/recruitment' },
    { title: 'Active Probations', value: stats.activeProbations, icon: <Assessment />, color: '#7A5C3A', bg: '#F5EFE8', path: '/performance' },
  ];

  const daysUntil = (dateStr: string) => {
    const diff = new Date(dateStr).getTime() - Date.now();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" height="400px"><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Header */}
      <Box mb={3}>
        <Typography variant="h5" fontWeight="bold" color="text.primary">
          {greeting}, {user?.employee?.nickname || user?.employee?.firstName || user?.email?.split('@')[0] || 'there'}! 👋
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Here's what's happening across your organisation today.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Stat Cards */}
        {statCards.map((card) => (
          <Grid key={card.title} size={{ xs: 12, sm: 6, lg: 3 }}>
            <Card
              sx={{ cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 16px rgba(196,98,29,0.15)' } }}
              onClick={() => navigate(card.path)}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                      {card.title}
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" color={card.color} mt={0.5}>
                      {card.value}
                    </Typography>
                  </Box>
                  <Box sx={{ bgcolor: card.bg, borderRadius: 2, p: 1, color: card.color }}>
                    {card.icon}
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                  <TrendingUp fontSize="small" sx={{ color: card.color, fontSize: 14 }} />
                  <Typography variant="caption" color="text.secondary">View details</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {/* Recent Activities — wider, taller */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 2.5, height: 520, display: 'flex', flexDirection: 'column' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6" fontWeight="bold">Recent Activity</Typography>
              <Chip label={`${recentActivities.length} events`} size="small" sx={{ bgcolor: '#F3E6CD', color: '#C08A2E', fontWeight: 600 }} />
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              {recentActivities.length === 0 ? (
                <Box textAlign="center" py={6} color="text.secondary">
                  <Typography variant="body2">No recent activity yet</Typography>
                </Box>
              ) : (
                <List dense disablePadding>
                  {recentActivities.map((activity, i) => (
                    <React.Fragment key={activity.id}>
                      {i > 0 && <Divider />}
                      <Box py={1.5} display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box display="flex" gap={1.5} alignItems="flex-start" flex={1}>
                          <Avatar sx={{ width: 28, height: 28, bgcolor: '#F3E6CD', color: '#C08A2E', fontSize: 13, flexShrink: 0, mt: 0.25 }}>
                            {(activity.description?.[0] || '?').toUpperCase()}
                          </Avatar>
                          <ListItemText
                            primary={activity.description}
                            secondary={new Date(activity.createdAt).toLocaleString()}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                        </Box>
                        <Chip
                          label={activity.type?.replace(/_/g, ' ')}
                          size="small"
                          sx={{ ml: 1, flexShrink: 0, bgcolor: '#F3E6CD', color: '#C08A2E', fontWeight: 600, fontSize: '0.65rem' }}
                        />
                      </Box>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Due Requests & Reminders */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 2.5, height: 520, display: 'flex', flexDirection: 'column' }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Badge badgeContent={pendingLeaves.length + probations.length} color="error" max={99}>
                <NotificationsActive sx={{ color: '#C08A2E' }} />
              </Badge>
              <Typography variant="h6" fontWeight="bold">Due Requests</Typography>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto' }}>

              {/* Pending Leave Approvals */}
              {pendingLeaves.length > 0 && (
                <>
                  <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                    <HourglassTop sx={{ fontSize: 14, color: '#9E6F22' }} />
                    <Typography variant="caption" fontWeight={700} color="#9E6F22" textTransform="uppercase" letterSpacing={0.5}>
                      Pending Leave Approvals
                    </Typography>
                  </Box>
                  <List dense disablePadding sx={{ mb: 2 }}>
                    {pendingLeaves.map((leave, i) => (
                      <React.Fragment key={leave.id}>
                        {i > 0 && <Divider />}
                        <ListItemButton
                          onClick={() => navigate('/leave')}
                          sx={{ borderRadius: 1.5, py: 1, px: 1, '&:hover': { bgcolor: '#F3E6CD' } }}
                        >
                          <Box flex={1}>
                            <Typography variant="body2" fontWeight={600}>
                              {displayName(leave.employee)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {leave.leaveType?.name || 'Leave'} · {new Date(leave.startDate).toLocaleDateString()} – {new Date(leave.endDate).toLocaleDateString()}
                            </Typography>
                          </Box>
                          <ArrowForward fontSize="small" sx={{ color: '#C08A2E', opacity: 0.5, flexShrink: 0 }} />
                        </ListItemButton>
                      </React.Fragment>
                    ))}
                  </List>
                </>
              )}

              {/* Active Probations */}
              {probations.length > 0 && (
                <>
                  <Divider sx={{ mb: 1.5 }} />
                  <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                    <CheckCircleOutline sx={{ fontSize: 14, color: '#2E7D5B' }} />
                    <Typography variant="caption" fontWeight={700} color="#2E7D5B" textTransform="uppercase" letterSpacing={0.5}>
                      Active Probations
                    </Typography>
                  </Box>
                  <List dense disablePadding>
                    {probations.map((prob, i) => {
                      const days = daysUntil(prob.endDate);
                      return (
                        <React.Fragment key={prob.id}>
                          {i > 0 && <Divider />}
                          <ListItemButton
                            onClick={() => navigate('/performance')}
                            sx={{ borderRadius: 1.5, py: 1, px: 1, '&:hover': { bgcolor: '#EAF3EC' } }}
                          >
                            <Box flex={1}>
                              <Typography variant="body2" fontWeight={600}>
                                {displayName(prob.employee)}
                              </Typography>
                              <Typography variant="caption" color={days <= 14 ? 'error' : 'text.secondary'}>
                                {days > 0 ? `Ends in ${days} day${days !== 1 ? 's' : ''}` : 'Overdue'}
                              </Typography>
                            </Box>
                            <Chip
                              label={days <= 14 ? 'Soon' : 'Active'}
                              size="small"
                              color={days <= 14 ? 'warning' : 'default'}
                              sx={{ fontSize: '0.65rem', flexShrink: 0 }}
                            />
                          </ListItemButton>
                        </React.Fragment>
                      );
                    })}
                  </List>
                </>
              )}

              {pendingLeaves.length === 0 && probations.length === 0 && (
                <Box textAlign="center" py={6} color="text.secondary">
                  <CheckCircleOutline sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                  <Typography variant="body2">All clear — nothing pending!</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
